<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <!-- Toast Notification -->
    <transition name="toast">
      <div v-if="globalError" class="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full px-4">
        <div :class="[
          'rounded-xl shadow-2xl p-4 backdrop-blur-lg border flex items-center gap-3',
          globalErrorType === 'success' 
            ? 'bg-green-50/90 border-green-200 text-green-800' 
            : 'bg-red-50/90 border-red-200 text-red-800'
        ]">
          <svg v-if="globalErrorType === 'success'" class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          <svg v-else class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
          </svg>
          <span class="flex-1 font-medium text-sm">{{ globalError }}</span>
          <button @click="globalError = ''" class="text-current opacity-60 hover:opacity-100 transition-opacity">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>
    </transition>

    <!-- Honeypot -->
    <input type="text" v-model="honeypot" style="position:absolute;left:-9999px;opacity:0;height:0;width:0;" tabindex="-1" autocomplete="off" aria-hidden="true" />

    <transition name="fade" mode="out-in">

      <!-- FORM -->
      <div v-if="!submitted" key="form" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <header class="text-center mb-8 md:mb-2 mt-8 md:mt-12">
          <h1 class="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {{ $t('cyber.formTitle') }} <span class="text-[#144A78]">{{ $t('cyber.formTitleHighlight') }}</span>
          </h1>
          <p class="text-gray-600 text-base md:text-lg max-w-2xl mx-auto">{{ $t('cyber.formSubtitle') }}</p>
        </header>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div class="lg:col-span-2 space-y-6">

            <!-- Contact Section -->
            <div class="bg-white rounded-xl shadow-md border border-gray-100">
              <div class="bg-gradient-to-r from-[#144A78] to-[#0a192f] px-6 py-4 rounded-t-xl">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  </div>
                  <div>
                    <h2 class="text-lg font-semibold text-white text-left">{{ $t('cyber.step1Title') }}</h2>
                    <p class="text-sm text-blue-100 text-left">{{ $t('cyber.step1Desc') }}</p>
                  </div>
                </div>
              </div>

              <div class="p-6 space-y-5">
                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1.5 text-left">{{ $t('cyberForm.organisation') }}</label>
                  <input v-model="form.organisation" type="text" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A78] focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-400 text-left" :placeholder="$t('cyberForm.organisation')" maxlength="200" />
                </div>

                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1.5 text-left">{{ $t('cyber.fullName') }}</label>
                  <input v-model="form.full_name" type="text" :class="['w-full px-4 py-3 border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 text-left', errors.full_name ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-transparent' : 'border-gray-300 focus:ring-2 focus:ring-[#144A78] focus:border-transparent']" :placeholder="$t('cyber.fullNamePh')" maxlength="150" @input="errors.full_name = ''" />
                  <p v-if="errors.full_name" class="mt-1.5 text-sm text-red-600 text-left">{{ errors.full_name }}</p>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <!-- Phone with Flag Picker -->
                  <div>
                    <label class="block text-sm font-bold text-gray-700 mb-1.5 text-left">{{ $t('cyber.phone') }}</label>
                    <div class="phone-wrapper" @click.stop>
                      <button type="button" class="phone-trigger" @click="toggleCountryDropdown">
                        <img :src="getFlagUrl(selectedCountry.code)" :alt="selectedCountry.code" class="flag-img" @error="onFlagError" />
                        <span class="phone-dial-code">+{{ selectedCountry.dial_code }}</span>
                        <svg :class="['phone-chevron', showCountryDropdown && 'phone-chevron--open']" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
                      </button>
                      <input v-model="form.phone" type="tel" class="phone-input" placeholder="000 000 000" maxlength="20" />
                      <transition name="dropdown">
                        <div v-if="showCountryDropdown" class="country-dropdown">
                          <div class="country-search-wrap">
                            <svg class="country-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                            <input v-model="countrySearch" type="text" class="country-search-input" :placeholder="$t('cyber.searchCountry') || 'Search...'" @click.stop ref="countrySearchInput" />
                          </div>
                          <div class="country-list">
                            <button v-for="c in filteredCountries" :key="c.code" type="button" :class="['country-item', c.code === selectedCountry.code && 'country-item--active']" @click="selectCountry(c)">
                              <img :src="getFlagUrl(c.code)" :alt="c.code" class="flag-img" @error="onFlagError" />
                              <span class="country-item-name">{{ c.name }}</span>
                              <span class="country-item-code">+{{ c.dial_code }}</span>
                            </button>
                            <div v-if="filteredCountries.length === 0" class="country-empty">{{ $t('cyber.noResults') || 'No results found' }}</div>
                          </div>
                        </div>
                      </transition>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-bold text-gray-700 mb-1.5 text-left">{{ $t('cyber.email') }}</label>
                    <input v-model="form.email" type="email" :class="['w-full px-4 py-3 border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 text-left', errors.email ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-transparent' : 'border-gray-300 focus:ring-2 focus:ring-[#144A78] focus:border-transparent']" :placeholder="$t('cyber.emailPh')" maxlength="254" @input="errors.email = ''" />
                    <p v-if="errors.email" class="mt-1.5 text-sm text-red-600 text-left">{{ errors.email }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Incident Section -->
            <div class="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
              <div class="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/></svg>
                  </div>
                  <div>
                    <h2 class="text-lg font-semibold text-white text-left">{{ $t('cyber.step2Title') }}</h2>
                    <p class="text-sm text-red-50 text-left">{{ $t('cyber.step2Desc') }}</p>
                  </div>
                </div>
              </div>

              <div class="p-6 space-y-5">
                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1.5 text-left">{{ $t('cyberForm.incidentSubject') }}</label>
                  <input v-model="form.incident_subject" type="text" :class="['w-full px-4 py-3 border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 text-left', errors.incident_subject ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-transparent' : 'border-gray-300 focus:ring-2 focus:ring-[#144A78] focus:border-transparent']" :placeholder="$t('cyberForm.incidentSubject')" maxlength="300" @input="errors.incident_subject = ''" />
                  <p v-if="errors.incident_subject" class="mt-1.5 text-sm text-red-600 text-left">{{ errors.incident_subject }}</p>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label class="block text-sm font-bold text-gray-700 mb-1.5 text-left">{{ $t('cyberForm.incidentNature') }}</label>
                    <div class="relative">
                      <select v-model="form.incident_nature" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A78] focus:border-transparent transition-all duration-200 text-gray-900 appearance-none bg-white cursor-pointer text-left">
                        <option value="">{{ $t('cyberForm.select') }}</option>
                        <option v-for="n in incidentNatures" :key="n.value" :value="n.value">{{ $t(n.label) }}</option>
                      </select>
                      <svg class="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
                    </div>
                  </div>
                  <div>
                    <label class="block text-sm font-bold text-gray-700 mb-1.5 text-left">{{ $t('cyber.incidentDate') }}</label>

                    <input v-model="form.incident_date" type="date" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A78] focus:border-transparent transition-all duration-200 text-gray-900 text-left" :max="todayDate" />
                  </div>
                </div>

                <!-- Other nature specification -->
                <transition name="fade">
                  <div v-if="form.incident_nature === 'other'" class="other-nature-wrap">
                    <label class="block text-sm font-bold text-gray-700 mb-1.5 text-left">
                      {{ $t('cyberForm.otherNatureLabel') || 'Please specify the nature of the incident' }}
                    </label>
                    <input
                      v-model="form.other_nature"
                      type="text"
                      :class="[
                        'w-full px-4 py-3 border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 text-left',
                        errors.other_nature
                          ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-transparent'
                          : 'border-gray-300 focus:ring-2 focus:ring-[#144A78] focus:border-transparent'
                      ]"
                      :placeholder="$t('cyberForm.otherNaturePlaceholder') || 'e.g. SIM swapping, insider threat...'"
                      maxlength="200"
                      @input="errors.other_nature = ''"
                    />
                    <p v-if="errors.other_nature" class="mt-1.5 text-sm text-red-600 text-left">{{ errors.other_nature }}</p>
                  </div>
                </transition>

                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1.5 text-left">{{ $t('cyberForm.affectedAssets') }}</label>
                  <textarea v-model="form.affected_assets" rows="3" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#144A78] focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-400 resize-none text-left" :placeholder="$t('cyberForm.affectedAssets')" maxlength="2000"></textarea>
                </div>

                <div>
                  <label class="block text-sm font-bold text-gray-700 mb-1.5 text-left">{{ $t('cyber.description') }}</label>
                  <textarea v-model="form.description" rows="5" :class="['w-full px-4 py-3 border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 resize-none text-left', errors.description ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-transparent' : 'border-gray-300 focus:ring-2 focus:ring-[#144A78] focus:border-transparent']" :placeholder="$t('cyber.descriptionPh')" maxlength="5000" @input="errors.description = ''"></textarea>
                  <p v-if="errors.description" class="mt-1.5 text-sm text-red-600 text-left">{{ errors.description }}</p>
                  <p class="mt-1.5 text-xs text-gray-500 text-right">{{ form.description.length }} / 5000</p>
                </div>
              </div>
            </div>

            <!-- Submit -->
            <div class="flex justify-center pt-2">
              <button @click="submitForm" :disabled="submitting" :class="['px-8 py-4 rounded-lg font-semibold text-white transition-all duration-300 flex items-center gap-3 shadow-lg', submitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-[#144A78] to-[#0a192f] hover:shadow-xl hover:scale-105']">
                <svg v-if="submitting" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
                <span class="text-base">{{ submitting ? $t('cyber.submitting') : $t('cyber.submitReport') }}</span>
              </button>
            </div>
          </div>

          <!-- Sidebar -->
          <aside class="lg:col-span-1">
            <div class="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden sticky top-24">
              <div class="bg-gradient-to-br from-[#144A78] to-[#0a192f] p-6">
                <div class="flex items-center gap-4">
                  <div class="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                  </div>
                  <h3 class="text-lg font-semibold text-white text-left">{{ $t('cyberForm.sidebarTitle') }}</h3>
                </div>
              </div>
              <div class="p-6 space-y-3">
                <div class="group">
                  <div class="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 border border-transparent hover:border-blue-100">
                    <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-[#144A78] to-[#0a192f] flex items-center justify-center flex-shrink-0 shadow-sm">
                      <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 text-left">{{ $t('cyberForm.viaEmail') }}</p>
                      <a href="mailto:alerts@cirt.cm" class="text-base font-medium text-[#144A78] hover:text-[#0a192f] break-all transition-colors duration-200 block text-left truncate">alerts@cirt.cm</a>
                    </div>
                  </div>
                </div>
                <div class="group">
                  <div class="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 border border-transparent hover:border-blue-100">
                    <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-[#144A78] to-[#0a192f] flex items-center justify-center flex-shrink-0 shadow-sm">
                      <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                    </div>
                    <div class="flex-1 space-y-2">
                      <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 text-left">{{ $t('cyberForm.viaPhone') }}</p>
                      <div class="flex items-center gap-2">
                        <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                        <a href="tel:+237222207909" class="text-base font-medium text-[#144A78] hover:text-[#0a192f] transition-colors duration-200">+237 222 207 909</a>
                      </div>
                      <div class="flex items-center gap-2">
                        <svg class="w-10 h-10 text-green-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.45-1.272.607-1.446c.157-.174.347-.218.463-.218l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.087-.177.181-.076.355.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86.174.087.276.072.378-.043.101-.116.433-.506.549-.68.116-.173.232-.145.39-.087.159.058 1.003.473 1.175.559.172.086.287.129.33.202.043.073.043.423-.101.828z"/></svg>
                        <a href="https://wa.me/237620050515" target="_blank" rel="noopener noreferrer" class="text-base font-medium text-gray-600 hover:text-green-700 transition-colors duration-200">+237 620 050 515</a>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="group">
                  <div class="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 border border-transparent hover:border-blue-100">
                    <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-[#144A78] to-[#0a192f] flex items-center justify-center flex-shrink-0 shadow-sm">
                      <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 text-left">{{ $t('cyberForm.website') }}</p>
                      <a href="https://www.cirt.cm" target="_blank" rel="noopener noreferrer" class="text-base font-medium text-[#144A78] hover:text-[#0a192f] break-all transition-colors duration-200 block text-left truncate">https://www.cirt.cm</a>
                    </div>
                  </div>
                </div>
                <div class="relative pt-3 mt-3">
                  <div class="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                  <div class="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 shadow-sm">
                    <div class="flex items-start gap-3">
                      <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-[#144A78] to-[#0a192f] flex items-center justify-center flex-shrink-0 shadow-sm">
                        <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      </div>
                      <p class="text-sm text-blue-900 leading-relaxed text-left font-medium">{{ $t('cyberForm.headerText') }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <!-- SUCCESS -->
      <div v-else key="success" class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div class="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 md:p-12 text-center">
          <div class="inline-flex items-center justify-center mb-6">
            <div class="relative">
              <div class="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-20"></div>
              <div class="relative w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>
              </div>
            </div>
          </div>
          <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{{ $t('cyberForm.successTitle') }}</h1>
          <p class="text-gray-600 text-base md:text-lg mb-8 max-w-xl mx-auto">{{ $t('cyberForm.successDesc') }}</p>
          <div class="inline-block bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6 mb-8">
            <p class="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">{{ $t('cyberForm.referenceLabel') }}</p>
            <p class="text-3xl font-bold text-[#144A78] font-mono tracking-wider">{{ referenceNumber }}</p>
            <p class="text-xs text-gray-600 mt-2">{{ $t('cyberForm.referenceHint') }}</p>
          </div>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <button @click="resetForm" class="px-6 py-3 bg-gradient-to-r from-[#144A78] to-[#0a192f] text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 hover:scale-105">{{ $t('cyberForm.newReport') }}</button>
            <router-link :to="getRoute('home')" class="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-[#144A78] hover:text-[#144A78] transition-all duration-300">{{ $t('cyberForm.backHome') }}</router-link>
          </div>
          <div class="mt-8 pt-8 border-t border-gray-200">
            <div class="flex items-center justify-center gap-2 text-sm text-gray-500">
              <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
              <span>{{ $t('cyberForm.securedBy') }}</span>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { sanitizeInput, isValidEmail } from '@/utils/security-utils';
import { executeRecaptcha, loadRecaptcha } from '@/utils/recaptcha';
import { countries, getFlag } from '@/utils/countries';
import getAPI from '@/axios-api';

export default {
  name: 'CyberIncidentForm',
  data() {
    return {
      honeypot: '',
      submitting: false,
      submitted: false,
      globalError: '',
      globalErrorType: 'error',
      referenceNumber: null,
      showCountryDropdown: false,
      countrySearch: '',
      errors: {},
      countries,
      selectedCountry: countries.find(c => c.code === 'CM') || countries[0],
      form: {
        organisation: '',
        full_name: '',
        phone: '',
        email: '',
        incident_subject: '',
        incident_nature: '',
        other_nature: '',
        incident_date: '',
        affected_assets: '',
        description: '',
      },
      incidentNatures: [
        { value: 'identity_theft', label: 'cyberForm.natureIdentityTheft' },
        { value: 'scamming', label: 'cyberForm.natureScamming' },
        { value: 'phishing', label: 'cyberForm.naturePhishing' },
        { value: 'account_hijacking', label: 'cyberForm.natureAccountHijack' },
        { value: 'cyber_harassment', label: 'cyberForm.natureCyberHarassment' },
        { value: 'malware', label: 'cyberForm.natureMalware' },
        { value: 'data_breach', label: 'cyberForm.natureDataBreach' },
        { value: 'web_defacement', label: 'cyberForm.natureWebDefacement' },
        { value: 'denial_of_service', label: 'cyberForm.natureDos' },
        { value: 'ransomware', label: 'cyberForm.natureRansomware' },
        { value: 'other', label: 'cyberForm.natureOther' },
      ],
    };
  },
  computed: {
    currentLang() {
      return this.$route.params.lang || this.$i18n.locale || 'en';
    },
    todayDate() {
      return new Date().toISOString().split('T')[0];
    },
    filteredCountries() {
      if (!this.countrySearch.trim()) return this.countries;
      const q = this.countrySearch.toLowerCase().trim();
      return this.countries.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.dial_code.includes(q) ||
        c.code.toLowerCase().includes(q)
      );
    }
  },
  methods: {
    getFlagUrl(code) {
      return getFlag(code, 40);
    },

    onFlagError(e) {
      e.target.style.display = 'none';
      if (!e.target.nextElementSibling || !e.target.nextElementSibling.classList.contains('flag-fallback')) {
        var fallback = document.createElement('span');
        fallback.textContent = e.target.alt || '??';
        fallback.className = 'flag-fallback';
        e.target.parentNode.insertBefore(fallback, e.target.nextSibling);
      }
    },

    toggleCountryDropdown() {
      this.showCountryDropdown = !this.showCountryDropdown;
      if (this.showCountryDropdown) {
        this.countrySearch = '';
        this.$nextTick(function() {
          if (this.$refs.countrySearchInput) this.$refs.countrySearchInput.focus();
        });
      }
    },

    selectCountry(c) {
      this.selectedCountry = c;
      this.showCountryDropdown = false;
      this.countrySearch = '';
    },

    validate() {
      this.errors = {};
      var valid = true;
      if (!this.form.full_name.trim()) { this.errors.full_name = this.$t('cyber.errRequired'); valid = false; }
      if (!this.form.email.trim()) { this.errors.email = this.$t('cyber.errRequired'); valid = false; }
      else if (!isValidEmail(this.form.email)) { this.errors.email = this.$t('cyber.errValidEmail'); valid = false; }
      if (!this.form.incident_subject.trim()) { this.errors.incident_subject = this.$t('cyber.errRequired'); valid = false; }
      if (this.form.incident_nature === 'other' && !this.form.other_nature.trim()) { this.errors.other_nature = this.$t('cyber.errRequired'); valid = false; }
      if (!this.form.description.trim() || this.form.description.trim().length < 10) { this.errors.description = this.$t('cyber.errDescMin'); valid = false; }
      return valid;
    },

    async submitForm() {
      if (this.honeypot) return;
      if (!this.validate()) {
        this.$nextTick(function() {
          var el = document.querySelector('[class*="border-red"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      }
      this.submitting = true;
      try {
        var recaptchaToken = null;
        try { recaptchaToken = await executeRecaptcha('cyber_incident_submit'); } catch (e) { /* fallback */ }
        var phone = this.form.phone ? '+' + this.selectedCountry.dial_code + this.form.phone.replace(/^0+/, '') : '';
        var payload = {
          organisation: sanitizeInput(this.form.organisation),
          full_name: sanitizeInput(this.form.full_name),
          phone: sanitizeInput(phone),
          email: sanitizeInput(this.form.email).toLowerCase(),
          incident_subject: sanitizeInput(this.form.incident_subject),
          incident_nature: this.form.incident_nature,
          other_nature: this.form.incident_nature === 'other' ? sanitizeInput(this.form.other_nature) : '',
          incident_date: this.form.incident_date || null,
          affected_assets: sanitizeInput(this.form.affected_assets),
          detailed_description: sanitizeInput(this.form.description),
        };
        if (recaptchaToken) payload.recaptcha_token = recaptchaToken;
        var response = await getAPI.post('/api/cyber-incident/', payload, { timeout: 30000 });
        if (response.data && response.data.success) {
          this.referenceNumber = response.data.reference_id;
          this.submitted = true;
          this.showMessage(this.$t('cyber.submitSuccess'), 'success');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          this.showMessage((response.data && response.data.error) || this.$t('cyber.submitError'));
        }
      } catch (error) {
        var msg = this.$t('cyber.submitError');
        if (error.response && error.response.status === 429) { msg = this.$t('cyber.errRateLimit'); }
        else if (error.response && error.response.data && error.response.data.error) { msg = error.response.data.error; }
        else if (error.response && error.response.data && error.response.data.details) {
          var details = error.response.data.details;
          var firstKey = Object.keys(details)[0];
          msg = Array.isArray(details[firstKey]) ? details[firstKey][0] : String(details[firstKey]);
        } else if (!error.response && error.request) { msg = this.$t('cyber.errors.network'); }
        this.showMessage(msg);
      } finally {
        this.submitting = false;
      }
    },

    showMessage(msg, type) {
      type = type || 'error';
      this.globalError = msg;
      this.globalErrorType = type;
      var self = this;
      setTimeout(function() { if (self.globalError === msg) self.globalError = ''; }, 6000);
    },

    resetForm() {
      this.form = { organisation: '', full_name: '', phone: '', email: '', incident_subject: '', incident_nature: '', other_nature: '', incident_date: '', affected_assets: '', description: '' };
      this.errors = {};
      this.submitted = false;
      this.referenceNumber = null;
    },

    handleOutsideClick(e) {
      if (this.showCountryDropdown && !e.target.closest('.phone-wrapper')) {
        this.showCountryDropdown = false;
      }
    },

    getRoute(name) {
      var lang = this.$route.params.lang || this.$i18n.locale || 'en';
      return { name: name, params: { lang: lang } };
    }
  },

  mounted() {
    loadRecaptcha().catch(function() {});
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  }
};
</script>

<style scoped>
/* PHONE / COUNTRY PICKER */
.phone-wrapper {
  position: relative;
  display: flex;
  align-items: stretch;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #fff;
  overflow: visible;
  transition: border-color 0.2s, box-shadow 0.2s;
  z-index: 20;
}
.phone-wrapper:focus-within {
  border-color: #144A78;
  box-shadow: 0 0 0 2px rgba(20, 74, 120, 0.15);
}
.phone-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  border: none;
  border-right: 1px solid #e5e7eb;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}
.phone-trigger:hover { background: #f9fafb; }

.flag-img {
  width: 24px;
  height: 16px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.08);
  flex-shrink: 0;
}
.flag-fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 16px;
  background: #e5e7eb;
  border-radius: 2px;
  font-size: 9px;
  font-weight: 700;
  color: #6b7280;
  flex-shrink: 0;
}
.phone-dial-code {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}
.phone-chevron {
  width: 14px;
  height: 14px;
  color: #9ca3af;
  transition: transform 0.2s;
}
.phone-chevron--open { transform: rotate(180deg); }

.phone-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 14px;
  font-size: 0.9rem;
  font-family: inherit;
  color: #111827;
  background: transparent;
  min-width: 0;
}
.phone-input::placeholder { color: #9ca3af; }

/* Country Dropdown */
.country-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 320px;
  width: max-content;
  max-width: 400px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08);
  z-index: 9999;
  overflow: hidden;
}
.country-search-wrap {
  position: relative;
  background: #fff;
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
}
.country-search-icon {
  position: absolute;
  left: 22px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: #9ca3af;
  pointer-events: none;
}
.country-search-input {
  width: 100%;
  padding: 8px 12px 8px 32px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #111827;
  outline: none;
  background: #f9fafb;
  transition: border-color 0.15s;
}
.country-search-input:focus { border-color: #144A78; background: #fff; }
.country-search-input::placeholder { color: #9ca3af; }

.country-list { max-height: 300px; overflow-y: auto; }
.country-list::-webkit-scrollbar { width: 5px; }
.country-list::-webkit-scrollbar-track { background: #f9fafb; }
.country-list::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }

.country-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: none;
  font-size: 0.85rem;
  color: #111827;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
}
.country-item:hover { background: #f0f4ff; }
.country-item--active { background: #eef2ff; font-weight: 600; }
.country-item-name { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.country-item-code { color: #9ca3af; font-size: 0.8rem; font-variant-numeric: tabular-nums; flex-shrink: 0; }
.country-empty { padding: 20px 14px; text-align: center; color: #9ca3af; font-size: 0.85rem; }

/* OTHER NATURE */
.other-nature-wrap {
  padding: 4px 0;
}

/* ANIMATIONS */
@keyframes ping { 75%, 100% { transform: scale(2); opacity: 0; } }
.animate-ping { animation: ping 1.5s cubic-bezier(0,0,0.2,1) infinite; }

/* TRANSITIONS */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.toast-enter-active { transition: all 0.35s cubic-bezier(0.34,1.56,0.64,1); }
.toast-leave-active { transition: all 0.25s ease; }
.toast-enter-from { opacity: 0; transform: translateX(-50%) translateY(-16px) scale(0.96); }
.toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(-10px); }
.dropdown-enter-active { transition: all 0.2s cubic-bezier(0.34,1.56,0.64,1); }
.dropdown-leave-active { transition: all 0.15s ease; }
.dropdown-enter-from { opacity: 0; transform: translateY(-8px) scale(0.97); }
.dropdown-leave-to { opacity: 0; transform: translateY(-4px); }

/* SCROLLBAR */
.overflow-y-auto::-webkit-scrollbar { width: 6px; }
.overflow-y-auto::-webkit-scrollbar-track { background: #f3f4f6; border-radius: 3px; }
.overflow-y-auto::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }

/* DATE / SELECT */
input[type="date"]::-webkit-calendar-picker-indicator { cursor: pointer; filter: opacity(0.6); }
input[type="date"]::-webkit-calendar-picker-indicator:hover { filter: opacity(1); }
select option { background: white; color: #111827; }

/* RESPONSIVE */
@media (max-width: 640px) {
  .sticky { position: relative; }
  .country-dropdown { min-width: 100%; max-width: 360px; }
}

/* ACCESSIBILITY */
@media (prefers-reduced-motion: reduce) {
  .animate-ping, .transition-all, .transition-colors, .transition-opacity, .transition-transform {
    transition: none !important;
    animation: none !important;
  }
}
button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible, a:focus-visible {
  outline: 2px solid #144A78;
  outline-offset: 2px;
}
</style>